// How the stadium is lit for a sun elevation: sky colours, sunlight, floodlights, fill, fog and
// colour grade. Pure numbers (linear RGB), no three.js, so it can be unit tested. Keys run from
// deep night to high noon; between keys every value is blended. The 4° key is the original
// golden-hour sunset of ui/index.md.

export type Rgb = [number, number, number];

export interface SkyPalette {
  zenithAway: Rgb; zenithSun: Rgb;
  upperAway: Rgb; upperSun: Rgb;
  horizonAway: Rgb; horizonSun: Rgb;
  /** Wide glow around the sun and the tighter halo. */
  glow: Rgb; halo: Rgb;
  cloudAway: Rgb; cloudSun: Rgb; cloudShadow: Rgb;
}

export interface Daylight {
  sky: SkyPalette;
  /** Sunlight colour and intensity (0 once the sun has set). */
  sunColour: Rgb; sunIntensity: number;
  /** Stadium floodlights, 0 (off) to 1 (full night). */
  flood: number;
  /** Hemisphere fill: sky colour and intensity. */
  fillSky: Rgb; fillIntensity: number;
  fog: Rgb;
  /** Image-based light strength (scene.environmentIntensity). */
  envIntensity: number;
  /** Stars in the sky, 0 to 1. */
  stars: number;
  /** Rim light on the cats' fur. */
  rim: Rgb;
  /** Colour grade: saturation, contrast, gain (with exposure) and the shadow tint. */
  grade: { saturation: number; contrast: number; gain: Rgb; shadowTint: Rgb };
}

type Key = Omit<Daylight, 'flood' | 'stars'> & { e: number };

const KEYS: Key[] = [
  { // Deep night: dark blue sky; floodlights do the lighting.
    e: -18,
    sky: {
      zenithAway: [0.004, 0.006, 0.016], zenithSun: [0.004, 0.006, 0.016],
      upperAway: [0.01, 0.014, 0.03], upperSun: [0.01, 0.014, 0.03],
      horizonAway: [0.022, 0.026, 0.045], horizonSun: [0.024, 0.026, 0.045],
      glow: [0, 0, 0], halo: [0, 0, 0],
      cloudAway: [0.03, 0.03, 0.04], cloudSun: [0.03, 0.03, 0.04], cloudShadow: [0.012, 0.013, 0.02],
    },
    sunColour: [0.5, 0.6, 0.9], sunIntensity: 0,
    fillSky: [0.42, 0.48, 0.66], fillIntensity: 0.42, fog: [0.018, 0.022, 0.032], envIntensity: 0.6,
    rim: [0.26, 0.29, 0.36],
    grade: { saturation: 1.1, contrast: 1.06, gain: [1.12, 1.12, 1.17], shadowTint: [0.92, 0.96, 1.12] },
  },
  { // Late twilight: the last blue in the west.
    e: -9,
    sky: {
      zenithAway: [0.008, 0.013, 0.04], zenithSun: [0.01, 0.016, 0.045],
      upperAway: [0.02, 0.03, 0.07], upperSun: [0.035, 0.04, 0.085],
      horizonAway: [0.04, 0.045, 0.08], horizonSun: [0.14, 0.09, 0.12],
      glow: [0.03, 0.015, 0.01], halo: [0, 0, 0],
      cloudAway: [0.04, 0.04, 0.06], cloudSun: [0.08, 0.05, 0.07], cloudShadow: [0.02, 0.02, 0.03],
    },
    sunColour: [0.6, 0.5, 0.7], sunIntensity: 0,
    fillSky: [0.42, 0.48, 0.68], fillIntensity: 0.42, fog: [0.035, 0.035, 0.05], envIntensity: 0.6,
    rim: [0.27, 0.29, 0.36],
    grade: { saturation: 1.12, contrast: 1.06, gain: [1.1, 1.1, 1.15], shadowTint: [0.92, 0.96, 1.12] },
  },
  { // Blue hour: the sun just below the horizon, a warm band under a deep blue sky.
    e: -3.5,
    sky: {
      zenithAway: [0.02, 0.035, 0.11], zenithSun: [0.03, 0.04, 0.12],
      upperAway: [0.05, 0.07, 0.17], upperSun: [0.2, 0.13, 0.22],
      horizonAway: [0.13, 0.13, 0.22], horizonSun: [0.7, 0.3, 0.18],
      glow: [0.2, 0.08, 0.04], halo: [0.15, 0.06, 0.02],
      cloudAway: [0.28, 0.16, 0.22], cloudSun: [0.75, 0.32, 0.2], cloudShadow: [0.07, 0.06, 0.11],
    },
    sunColour: [1.0, 0.4, 0.2], sunIntensity: 0,
    fillSky: [0.5, 0.52, 0.74], fillIntensity: 0.4, fog: [0.16, 0.11, 0.13], envIntensity: 0.6,
    rim: [0.35, 0.29, 0.3],
    grade: { saturation: 1.18, contrast: 1.07, gain: [1.06, 1.04, 1.1], shadowTint: [0.92, 0.96, 1.1] },
  },
  { // Sun on the horizon: deep red sunset.
    e: 0,
    sky: {
      zenithAway: [0.035, 0.06, 0.2], zenithSun: [0.09, 0.08, 0.24],
      upperAway: [0.12, 0.11, 0.3], upperSun: [0.72, 0.28, 0.22],
      horizonAway: [0.62, 0.32, 0.3], horizonSun: [1.8, 0.62, 0.2],
      glow: [0.8, 0.36, 0.13], halo: [2.6, 1.1, 0.4],
      cloudAway: [0.85, 0.32, 0.38], cloudSun: [2.3, 0.85, 0.3], cloudShadow: [0.22, 0.12, 0.24],
    },
    sunColour: [1.0, 0.5, 0.25], sunIntensity: 2.0,
    fillSky: [0.56, 0.6, 0.88], fillIntensity: 0.32, fog: [0.55, 0.22, 0.13], envIntensity: 0.55,
    rim: [0.52, 0.31, 0.18],
    grade: { saturation: 1.3, contrast: 1.1, gain: [1.07, 0.98, 0.9], shadowTint: [0.93, 0.97, 1.08] },
  },
  { // Golden hour: the original sunset look.
    e: 4,
    sky: {
      zenithAway: [0.05, 0.08, 0.26], zenithSun: [0.14, 0.1, 0.3],
      upperAway: [0.16, 0.14, 0.38], upperSun: [0.85, 0.36, 0.26],
      horizonAway: [1.0, 0.42, 0.3], horizonSun: [2.0, 0.85, 0.25],
      glow: [0.88, 0.47, 0.19], halo: [3.36, 1.82, 0.77],
      cloudAway: [1.05, 0.42, 0.45], cloudSun: [2.6, 1.2, 0.4], cloudShadow: [0.3, 0.17, 0.32],
    },
    sunColour: [1.0, 0.48, 0.18], sunIntensity: 4.2,
    fillSky: [0.28, 0.39, 0.81], fillIntensity: 0.3, fog: [0.69, 0.28, 0.14], envIntensity: 0.55,
    rim: [0.52, 0.34, 0.2],
    grade: { saturation: 1.32, contrast: 1.1, gain: [1.06, 0.99, 0.9], shadowTint: [0.93, 0.97, 1.08] },
  },
  { // Late afternoon: warm, low sun under a blue sky.
    e: 11,
    sky: {
      zenithAway: [0.06, 0.13, 0.4], zenithSun: [0.09, 0.15, 0.42],
      upperAway: [0.22, 0.3, 0.6], upperSun: [0.6, 0.5, 0.5],
      horizonAway: [0.78, 0.66, 0.6], horizonSun: [1.5, 1.0, 0.55],
      glow: [0.75, 0.55, 0.3], halo: [2.4, 1.8, 1.0],
      cloudAway: [1.1, 0.9, 0.85], cloudSun: [2.2, 1.6, 0.9], cloudShadow: [0.36, 0.33, 0.42],
    },
    sunColour: [1.0, 0.68, 0.42], sunIntensity: 4.5,
    fillSky: [0.32, 0.45, 0.85], fillIntensity: 0.34, fog: [0.55, 0.45, 0.4], envIntensity: 0.55,
    rim: [0.42, 0.34, 0.26],
    grade: { saturation: 1.2, contrast: 1.08, gain: [1.03, 1.0, 0.95], shadowTint: [0.94, 0.97, 1.06] },
  },
  { // Day: blue sky, white-yellow sun.
    e: 22,
    sky: {
      zenithAway: [0.06, 0.16, 0.5], zenithSun: [0.08, 0.19, 0.55],
      upperAway: [0.2, 0.36, 0.75], upperSun: [0.32, 0.46, 0.8],
      horizonAway: [0.62, 0.72, 0.9], horizonSun: [0.95, 0.92, 0.88],
      glow: [0.5, 0.46, 0.36], halo: [1.6, 1.45, 1.2],
      cloudAway: [1.15, 1.15, 1.2], cloudSun: [1.8, 1.7, 1.55], cloudShadow: [0.42, 0.46, 0.56],
    },
    sunColour: [1.0, 0.85, 0.68], sunIntensity: 4.6,
    fillSky: [0.4, 0.55, 0.9], fillIntensity: 0.36, fog: [0.5, 0.6, 0.78], envIntensity: 0.5,
    rim: [0.3, 0.29, 0.27],
    grade: { saturation: 1.12, contrast: 1.07, gain: [1.0, 1.0, 0.98], shadowTint: [0.96, 0.98, 1.04] },
  },
  { // High summer noon.
    e: 45,
    sky: {
      zenithAway: [0.05, 0.15, 0.52], zenithSun: [0.07, 0.18, 0.56],
      upperAway: [0.18, 0.35, 0.78], upperSun: [0.28, 0.44, 0.82],
      horizonAway: [0.6, 0.72, 0.92], horizonSun: [0.8, 0.86, 0.95],
      glow: [0.45, 0.43, 0.36], halo: [1.5, 1.4, 1.2],
      cloudAway: [1.2, 1.2, 1.25], cloudSun: [1.7, 1.65, 1.55], cloudShadow: [0.45, 0.5, 0.6],
    },
    sunColour: [1.0, 0.92, 0.8], sunIntensity: 4.6,
    fillSky: [0.42, 0.57, 0.92], fillIntensity: 0.36, fog: [0.5, 0.62, 0.82], envIntensity: 0.5,
    rim: [0.28, 0.28, 0.27],
    grade: { saturation: 1.1, contrast: 1.07, gain: [0.98, 0.99, 0.98], shadowTint: [0.96, 0.98, 1.04] },
  },
];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const smooth = (e0: number, e1: number, x: number) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

function mixKeys(a: Key, b: Key, t: number): Key {
  const sky = {} as SkyPalette;
  for (const k of Object.keys(a.sky) as (keyof SkyPalette)[]) sky[k] = mixRgb(a.sky[k], b.sky[k], t);
  return {
    e: lerp(a.e, b.e, t), sky,
    sunColour: mixRgb(a.sunColour, b.sunColour, t), sunIntensity: lerp(a.sunIntensity, b.sunIntensity, t),
    fillSky: mixRgb(a.fillSky, b.fillSky, t), fillIntensity: lerp(a.fillIntensity, b.fillIntensity, t),
    fog: mixRgb(a.fog, b.fog, t), envIntensity: lerp(a.envIntensity, b.envIntensity, t), rim: mixRgb(a.rim, b.rim, t),
    grade: {
      saturation: lerp(a.grade.saturation, b.grade.saturation, t), contrast: lerp(a.grade.contrast, b.grade.contrast, t),
      gain: mixRgb(a.grade.gain, b.grade.gain, t), shadowTint: mixRgb(a.grade.shadowTint, b.grade.shadowTint, t),
    },
  };
}

/** The light for a sun elevation in degrees. */
export function daylight(elevation: number): Daylight {
  let k: Key;
  if (elevation <= KEYS[0]!.e) k = KEYS[0]!;
  else if (elevation >= KEYS[KEYS.length - 1]!.e) k = KEYS[KEYS.length - 1]!;
  else {
    let i = 1;
    while (KEYS[i]!.e < elevation) i++;
    const a = KEYS[i - 1]!, b = KEYS[i]!;
    k = mixKeys(a, b, (elevation - a.e) / (b.e - a.e));
  }
  const { e: _e, ...rest } = k;
  // Direct sunlight fades out as the sun sets (the true horizon, with refraction, is at −0.83°):
  // a sun below the horizon casts no light or shadows.
  const sunIntensity = rest.sunIntensity * smooth(-0.9, 2, elevation);
  return { ...rest, sunIntensity, flood: smooth(3, -4, elevation), stars: smooth(-6, -12, elevation) };
}

/** A name for the light, for tests and the debug overlay. */
export function lightName(elevation: number): 'day' | 'golden' | 'twilight' | 'night' {
  if (elevation >= 8) return 'day';
  if (elevation >= -0.833) return 'golden';
  if (elevation >= -9) return 'twilight';
  return 'night';
}
